import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import * as z from 'zod';
import formMessages from '~/components/form-messages';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import moment from 'moment';
import { useStore } from '@tanstack/react-form';

import generalMessages from '~/containers/App/messages';
import messages from './messages';
import Tiptap from './TiptapField';
import { useAppForm } from '~/components/Form';

const isUrlAbsolute = (url: string) =>
  url.indexOf('://') > 0 || url.indexOf('//') === 0;

const createFormSchema = (intl: IntlShape) =>
  z.object({
    title: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, { message: intl.formatMessage(formMessages.required) }),
    validFrom: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, { message: intl.formatMessage(formMessages.required) }),
    validUntil: z.string().nullish(),
    content: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, { message: intl.formatMessage(formMessages.required) })
      .refine(
        (val) => {
          // Check for absolute URLs in images
          // This regex finds all image references in markdown. No idea how
          // it works; it's from https://stackoverflow.com/a/44227600
          const regexImages = /!\[[^\]]*\]\((.*?)\s*("(?:.*[^"])")?\s*\)/g;
          const images = val.match(regexImages);
          if (images) {
            // This regex extracts the url
            const regexUrl = /^.+\((.+)\)$/;
            const hasImageWithAbsoluteUrl = images.some((img) =>
              isUrlAbsolute(img.match(regexUrl)?.[1] || '')
            );
            return !hasImageWithAbsoluteUrl;
          }
          return true;
        },
        {
          message: intl.formatMessage(messages.externalImagesNotAllowed),
        }
      ),
    visibility: z.object({
      Publisher: z.boolean().default(false),
      ShiftLeader: z.boolean().default(false),
      Vip: z.boolean().default(false),
      Administrator: z.boolean(),
    }),
    attachments: z.array(
      z.object({ id: z.number(), fileName: z.string().optional() })
    ),
  });

type FormValues = z.input<ReturnType<typeof createFormSchema>>;

type NewsEntryFormProps = {
  onSubmit: (values: FormValues) => void;

  initialValues?: FormValues;
};

const NewsEntryForm = (props: NewsEntryFormProps) => {
  const intl = useIntl();
  const schema = createFormSchema(intl);
  const form = useAppForm({
    defaultValues: props.initialValues || {
      title: '',
      validFrom: moment().format('YYYY-MM-DDTHH:mm:ss'),
      validUntil: null as string | null | undefined,
      content: '',
      visibility: {
        Publisher: false,
        ShiftLeader: false,
        Vip: false,
        Administrator: true,
      },
      attachments: [] as FormValues['attachments'],
    },
    validators: {
      onSubmit: schema,
    },
    onSubmit: ({ value }) => {
      props.onSubmit(value);
    },
  });
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  return (
    <Box sx={{ m: 2 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Stack spacing={2}>
          <form.AppField name="title">
            {(field) => (
              <field.TextField
                label={intl.formatMessage(messages.title)}
                placeholder={intl.formatMessage(messages.title)}
              />
            )}
          </form.AppField>
          <form.AppField name="validFrom">
            {(field) => (
              <field.DateTimeField
                label={intl.formatMessage(messages.validFrom)}
              />
            )}
          </form.AppField>
          <form.AppField name="validUntil">
            {(field) => (
              <field.DateTimeField
                label={intl.formatMessage(messages.validUntil)}
              />
            )}
          </form.AppField>
          <form.Field name="content">
            {(contentField) => (
              <form.Field name="attachments" mode="array">
                {(attachmentsField) => (
                  <Tiptap
                    value={contentField.state.value}
                    onChange={(val) => contentField.handleChange(val)}
                    errorMessage={
                      contentField.state.meta.errors
                        .map((e) => e?.message ?? '')
                        .filter(Boolean)
                        .join(', ') || undefined
                    }
                    attachments={attachmentsField.state.value}
                    append={(attachment) =>
                      attachmentsField.pushValue(attachment)
                    }
                    remove={(index) => attachmentsField.removeValue(index)}
                  />
                )}
              </form.Field>
            )}
          </form.Field>
          <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
            <FormLabel component="legend">
              <FormattedMessage {...messages.visibility} />
            </FormLabel>
            <FormGroup>
              <form.Field name="visibility.Publisher">
                {(field) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="visibility.Publisher"
                        checked={field.state.value === true}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={intl.formatMessage(messages.publisher)}
                  />
                )}
              </form.Field>
              <form.Field name="visibility.ShiftLeader">
                {(field) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="visibility.ShiftLeader"
                        checked={field.state.value === true}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={intl.formatMessage(messages.shiftLeader)}
                  />
                )}
              </form.Field>
              <form.Field name="visibility.Vip">
                {(field) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="visibility.Vip"
                        checked={field.state.value === true}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={intl.formatMessage(messages.vip)}
                  />
                )}
              </form.Field>
              <form.Field name="visibility.Administrator">
                {(field) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="visibility.Administrator"
                        disabled
                        checked={field.state.value === true}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={intl.formatMessage(messages.administrator)}
                  />
                )}
              </form.Field>
            </FormGroup>
          </FormControl>
          <div>
            <Button variant="contained" size="large" type="submit">
              {isSubmitting ? (
                <FormattedMessage {...generalMessages.loading} />
              ) : (
                <FormattedMessage {...messages.save} />
              )}
            </Button>
          </div>
        </Stack>
      </form>
    </Box>
  );
};

export default NewsEntryForm;
